import React, { useMemo, useState, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-grid.css";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-theme-alpine.css";
import SearchBar from "../../../../SearchBarTable/SearchBarTable";
import { FaCubes } from "react-icons/fa";
// @ts-ignore CSS is loaded by the application bundler.
import "../../../AgGridTheme.css";
import SortHeader from "../../../SortingHeader";

import {
  ClientSideRowModelModule,
  ModuleRegistry,
  TextFilterModule,
  PaginationModule,
} from "ag-grid-community";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextFilterModule,
  PaginationModule,
]);

interface PogTableProps {
  pogs: any[];
  selectedRow: number | null;
  onRowSelect: (pog: any) => void;
}

const PogTable: React.FC<PogTableProps> = ({
  pogs,
  selectedRow,
  onRowSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const gridRef = useRef<AgGridReact>(null);

  // -----------------------------------------------------
  // SEARCH FILTER
  // -----------------------------------------------------
  const filteredPogs = useMemo(() => {
    const lower = searchQuery.toLowerCase();

    return pogs.filter((pog) => {
      const nameMatch =
        pog.pog_name?.toLowerCase().includes(lower);

      const poMatch =
        Array.isArray(pog.permission_objects) &&
        pog.permission_objects.some((po: any) =>
          po.permission_object_name
            .toLowerCase()
            .includes(lower)
        );

      return nameMatch || poMatch;
    });
  }, [pogs, searchQuery]);

  // -----------------------------------------------------
  // COLUMNS
  // -----------------------------------------------------
  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        width: 60,
        maxWidth: 60,
        checkboxSelection: true,
        sortable: false,
        filter: false,
        resizable: false,
        headerClass:
          "p-4 text-left text-base font-bold text-gray-700 bg-gray-200",
      },
      {
        headerName: "POG Name",
        field: "pog_name",
        sortable: true,
        flex: 1,
        minWidth: 250,
        autoHeight: true,
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2">
            {params.value}
          </div>
        ),
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "POG Name",
          enableSorting: true,
          icon: <FaCubes className="text-gray-600" />,
        },
        headerClass:
          "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
      },
      {
        headerName: "Permission Objects",
        field: "permission_objects",
        sortable: true,
        flex: 1,
        minWidth: 300,
        autoHeight: true,
        cellRenderer: (params: any) => {
          const pos = params.data.permission_objects;

          if (!Array.isArray(pos) || pos.length === 0)
            return "N/A";

          const rows = [];
          for (let i = 0; i < pos.length; i += 5) {
            rows.push(pos.slice(i, i + 5));
          }

          return (
            <div className="whitespace-normal break-words py-1">
              {rows.map((row: any[], idx: number) => (
                <div
                  key={idx}
                  className="flex flex-wrap gap-x-1"
                >
                  {row.map((po, index) => (
                    <span key={index}>
                      {po.permission_object_name}
                      {index < row.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          );
        },
        headerComponent: SortHeader,
        headerComponentParams: {
          displayName: "Permission Objects",
          enableSorting: true,
          icon: <FaCubes className="text-gray-600" />,
        },
        headerClass:
          "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      cellStyle: {
        padding: "8px 12px",
        fontSize: "16px",
        fontFamily: "'Poppins', sans-serif",
        color: "#374151",
      },
    }),
    []
  );

  const handleRowClicked = useCallback(
    (event: any) => {
      onRowSelect(event.data);
    },
    [onRowSelect]
  );

  return (
    <div className="p-4 bg-white shadow-md rounded-xl font-poppins">
      {/* SEARCH */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center bg-gray-200 px-3 py-2 rounded-full max-w-md">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            placeholder="Search POG..."
          />
        </div>
      </div>

      {/* GRID */}
      <div
        className="ag-theme-alpine font-poppins text-base text-gray-700"
        style={{
          height: "var(--admin-tab-grid-height, max(475px, calc(100dvh - 320px)))",
          width: "100%",
          overflow: "auto",
          border: "1px solid #E7E4FF",
          borderRadius: "8px",
        }}
      >
        <AgGridReact
          ref={gridRef}
          theme="legacy"
          rowData={filteredPogs}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection="single"
          rowHeight={60}
          headerHeight={55}
          pagination={true}
          paginationPageSize={100}
          animateRows
          onRowClicked={handleRowClicked}
          suppressRowClickSelection={false}
          domLayout="normal"
          getRowClass={(params) =>
            params.data.permission_object_group_id ===
            selectedRow
              ? "bg-gray-200"
              : ""
          }
          getRowHeight={(params) => {
            const count =
              params.data?.permission_objects?.length || 0;
            const lines = Math.ceil(count / 5);
            const base = 60;
            const lineHeight = 24;
            return base + (lines - 1) * lineHeight;
          }}
        />
      </div>
    </div>
  );
};

export default PogTable;
